import { PushSubscriptionPayload } from "@/generated-api/models/PushSubscriptionPayload";
import { usePushSubscribe, usePushUnsubscribe } from "@/tanstack-hooks";
import { useEffect, useState } from "react";
import { useToast } from "./use-toast";
import { PushUnsubscribePayload } from "@/generated-api";
import { browserVersion, isSafari } from "react-device-detect";
import type { UsePushNotificationsResult } from "./usePushNotifications.types";

// Extend Navigator for iOS push API (Declarative Web Push, Safari 18.4+)
declare global {
  interface Navigator {
    push?: {
      subscribe: (options: {
        userVisibleOnly: boolean;
        applicationServerKey: string;
      }) => Promise<PushSubscription>;
    };
  }
}

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

export const useVapidPushNotifications = (): UsePushNotificationsResult => {
  const { toast } = useToast();
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] =
    useState<NotificationPermission>("default");
  const [subscription, setSubscription] = useState<PushSubscription | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const pushSubscribeMutation = usePushSubscribe();
  const pushUnsubscribeMutation = usePushUnsubscribe();

  const handleSubscribe = (payload: PushSubscriptionPayload) => {
    pushSubscribeMutation.mutate(payload, {
      onError: (error) => {
        console.error(
          "Error - could not subscribe to push notifications",
          error
        );
        toast({
          variant: "destructive",
          title: "Could not subscribe to push notifications",
          description: "Try configuring your notifications from settings.",
          className: "dark",
        });
      },
    });
  };

  const handleUnsubscribe = (payload: PushUnsubscribePayload) => {
    pushUnsubscribeMutation.mutate(payload);
  };

  const checkExistingSubscription = async (
    setSubscription: (sub: PushSubscription | null) => void,
    setIsSupported: (supported: boolean) => void,
    setPermission: (perm: NotificationPermission) => void,
    setError: (err: string | null) => void
  ) => {
    const hasNotificationSupport =
      typeof window !== "undefined" && "Notification" in window;
    const hasPushSupport =
      hasNotificationSupport &&
      ("serviceWorker" in navigator || "push" in navigator);

    setIsSupported(hasPushSupport);
    if (!hasNotificationSupport) {
      setError("Notifications are not supported in this environment.");
      return;
    }

    setPermission(Notification.permission);

    try {
      // iOS 18.4+ (Declarative Web Push)
      if (
        "push" in navigator &&
        navigator.push &&
        Notification.permission === "granted"
      ) {
        // Note: Safari 18.4+ does not support retrieving an existing subscription,
        // so we call subscribe() again, which is safe if permission is granted.
        const subscription = await navigator.push.subscribe({
          userVisibleOnly: true,
          applicationServerKey: VAPID_PUBLIC_KEY,
        });
        setSubscription(subscription);
        return;
      }

      // Android + older iOS
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;
        const sub = await registration.pushManager.getSubscription();
        if (sub) {
          setSubscription(sub);
        }
      }
    } catch (err) {
      console.error("Error retrieving push subscription:", err);
      setError("Could not retrieve push subscription.");
    }
  };

  useEffect(() => {
    void checkExistingSubscription(
      setSubscription,
      setIsSupported,
      setPermission,
      setError
    ).finally(() => setIsInitialized(true));
  }, []);

  // Request permission and subscribe to push
  const requestNotificationPermission = async () => {
    if (!isSupported) {
      setError("Push notifications are not supported in this browser.");
      return;
    }

    try {
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult === "granted") {
        console.log("Notification permission granted.");
        await subscribeToPush();
      } else {
        setError("Notification permission denied.");
      }
    } catch (err) {
      const error = err as Error;
      setError("Failed to request notification permission: " + error.message);
    }
  };

  // Subscribe to push notifications (handles both iOS and Android)
  const subscribeToPush = async () => {
    try {
      let subscription: PushSubscription | null = null;

      if (!VAPID_PUBLIC_KEY) {
        throw new Error(
          "VITE_VAPID_PUBLIC_KEY is not defined in environment variables."
        );
      }

      // Prefer navigator.push (iOS 18.4+ Declarative Web Push)
      if ("push" in navigator && navigator.push) {
        subscription = await navigator.push.subscribe({
          userVisibleOnly: true,
          applicationServerKey: VAPID_PUBLIC_KEY,
        });
      }

      // Fallback to serviceWorker (Android and iOS 16.4 to 18.3)
      if (!subscription && "serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: VAPID_PUBLIC_KEY,
        });
      }

      // If neither condition is met, throw an error
      if (!subscription) {
        throw new Error("Push API not supported.");
      }

      const rawKey = subscription.getKey("p256dh");
      const rawAuth = subscription.getKey("auth");

      const payload: PushSubscriptionPayload = {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: rawKey
            ? btoa(String.fromCharCode(...new Uint8Array(rawKey)))
            : "",
          auth: rawAuth
            ? btoa(String.fromCharCode(...new Uint8Array(rawAuth)))
            : "",
        },
      };

      if (!subscription.endpoint || !rawKey || !rawAuth) {
        console.error("Incomplete push subscription: missing endpoint or keys");
        setError(
          "Push subscription failed: incomplete data returned by browser."
        );
        return;
      }

      setSubscription(subscription);
      handleSubscribe(payload);
    } catch (err) {
      const error = err as Error;
      setError("Subscription failed: " + error.message);
    }
  };

  // Unsubscribe from push notifications
  const unsubscribeFromPush = async () => {
    if (!subscription) return;

    const endpoint = subscription.endpoint;
    if (endpoint) {
      handleUnsubscribe({ endpoint });
    }

    const problematicSafariVersions = ["18.0", "18.1", "18.2", "18.3"];
    const isSafariWithUnsubscribeBug =
      isSafari &&
      problematicSafariVersions.some((v) => browserVersion.startsWith(v));
    if (isSafariWithUnsubscribeBug) {
      console.warn("Skipping unsubscribe() to prevent Safari 18.x crash");
      setSubscription(null);
      return;
    }

    try {
      await subscription.unsubscribe();
      setSubscription(null);
    } catch (err) {
      console.error("unsubscribe failed on browser:", err);
      setSubscription(null);
    }
  };

  return {
    isInitialized,
    isSupported,
    permission,
    subscription: subscription ? { endpoint: subscription.endpoint } : null,
    error,
    requestNotificationPermission,
    unsubscribeFromPush,
  };
};
